// Workout creation hook. The list/recent variants live in useStats.ts, which
// already exposes useRecentWorkouts.
import type { CreateWorkoutInput, UpdateWorkoutInput, Workout } from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

/** GET /v1/workouts/:id — single workout detail for the /history/:id page. */
export function useWorkout(id: string | undefined) {
  return useQuery({
    queryKey: ['workouts', 'detail', id],
    queryFn: () => api<Workout>(`/workouts/${id}`),
    enabled: !!id,
  });
}

/** PATCH /v1/workouts/:id — edit a logged workout (title, sets, etc.). */
export function useUpdateWorkout(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWorkoutInput) =>
      api<Workout>(`/workouts/${id}`, { method: 'PATCH', json: input }),
    onSuccess: (updated) => {
      qc.setQueryData(['workouts', 'detail', id], updated);
      void qc.invalidateQueries({ queryKey: ['workouts'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useCreateWorkout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkoutInput) =>
      api<Workout>('/workouts', { method: 'POST', json: input }),
    onSuccess: () => {
      // Recent workouts feed and stats both depend on the workouts list, so
      // wipe the lot — the affected queries are cheap to refetch.
      void qc.invalidateQueries({ queryKey: ['workouts'] });
      void qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}
